import React from 'react';
import SqftIcon from 'components/icon/sqft-icon';
import BathIcon from 'components/icon/bath-icon';
import BedIcon from 'components/icon/bed-icon';
import styles from './style.module.scss';
import { listingCardIds } from 'constants/test-constants';
import { Namespaces } from 'types/locales';
import { useTranslation } from 'next-i18next';
import { LOCALE_COMMON_PATHS } from 'types/locales';

export type BedBathSqftLabelsData = {
  bedrooms: number;
  bathrooms: number | null;
  avgSqft: number | null;
  sqftLabel: string;
};

export type BedBathSqftLabelsProps = {
  listing: BedBathSqftLabelsData;
  listingToCompare?: BedBathSqftLabelsData;
  /** When true, square footage is hidden and a dash is rendered instead. */
  hideSquareFootage?: boolean;
};

export default function BedBathSqftLabels({ listing, listingToCompare, hideSquareFootage }: BedBathSqftLabelsProps) {
  const { t } = useTranslation(Namespaces.COMMON);
  let differenceInBedrooms = 0;
  let differenceInBathrooms = 0;
  let differenceInSqft = 0;

  if (listingToCompare) {
    differenceInBedrooms = listing.bedrooms - listingToCompare.bedrooms;
    differenceInBathrooms = Number(listing.bathrooms) - Number(listingToCompare.bathrooms);
    differenceInSqft = Number(listing.avgSqft) - Number(listingToCompare.avgSqft);
  }

  const bedroomsLabel = t(LOCALE_COMMON_PATHS.LISTING_CARD_NUMBER_OF_BEDROOMS,
    { count: listing.bedrooms, difference: getDifferenceLabel(differenceInBedrooms),
      defaultValue: `${listing.bedrooms} Bd${getDifferenceLabel(differenceInBedrooms)}` });

  const bedroomsLongLabel = t(LOCALE_COMMON_PATHS.LISTING_CARD_NUMBER_OF_BEDROOMS_LONG,
    { count: listing.bedrooms, difference: getDifferenceLabel(differenceInBedrooms),
      defaultValue: `${listing.bedrooms} Bed${getDifferenceLabel(differenceInBedrooms)}` });

  const bathroomsLabel = t(LOCALE_COMMON_PATHS.LISTING_CARD_NUMBER_OF_BATHROOMS,
    { count: listing.bathrooms, difference: getDifferenceLabel(differenceInBathrooms),
      defaultValue: `${listing.bathrooms} Ba${getDifferenceLabel(differenceInBathrooms)}` });

  const bathroomsLongLabel = t(LOCALE_COMMON_PATHS.LISTING_CARD_NUMBER_OF_BATHROOMS_LONG,
    { count: listing.bathrooms, difference: getDifferenceLabel(differenceInBathrooms),
      defaultValue: `${listing.bathrooms} Bath${getDifferenceLabel(differenceInBathrooms)}` });

  return (
    <div className={styles.row}>
      <div>
        <p className={styles['listing-detail']} data-testid={listingCardIds.listingBeds}>
          <BedIcon className={styles.icon} />
          <span className={styles.label} data-short-label={bedroomsLabel} data-long-label={bedroomsLongLabel}/>
          <span className={styles[getArrowClassName(differenceInBedrooms)]} />
        </p>
        <p className={styles['listing-detail']} data-testid={listingCardIds.listingBathrooms}>
          <BathIcon className={styles.icon} />
          <span className={styles.label} data-short-label={bathroomsLabel} data-long-label={bathroomsLongLabel}/>
          <span className={styles[getArrowClassName(differenceInBathrooms)]} />
        </p>
        <p className={styles['listing-detail']} data-testid={listingCardIds.listingSqft}>
          <SqftIcon className={styles.icon} />
          {hideSquareFootage
            ? '-'
            : <>
              {t(LOCALE_COMMON_PATHS.LISTING_CARD_NUMBER_OF_SQFT,
                { count: listing?.avgSqft || 0, difference: getDifferenceLabel(differenceInSqft),
                  defaultValue: '{{count}} sqft' })}
              <span className={styles[getArrowClassName(differenceInSqft)]} />
            </>
          }
        </p>
      </div>
    </div>
  );
}

const getPlusMinusPrefix = (differenceInStat: number) => differenceInStat > 0 ? '+' : '';

const getArrowClassName = (differenceInStat: number) => {
  if (differenceInStat > 0) {
    return 'arrow-up';
  } else if (differenceInStat < 0) {
    return 'arrow-down';
  }
  return '';
};

const getDifferenceLabel = (differenceInStat: number) => {
  if (differenceInStat !== 0) {
    return `(${getPlusMinusPrefix(differenceInStat)}${differenceInStat})`;
  }
  return '';
};